import type { CSSProperties } from 'react';
import { BUILDING_DEFINITIONS, type BuildingType, type BuildingZone, type PlayerRace } from '@kaeliux/shared';
import { getBuildingIcon } from '../../utils/buildingIcons';
import { zoneAccentStyle } from './zoneAccent';
import { TileProgressCluster, type BuildingUpgradeProgress } from './TileProgressCluster';

/** A level 0→1 construct claiming this plot — queued behind another build, or already building. */
export interface PlotConstruct {
    buildingType: BuildingType;
    race: PlayerRace | undefined;
    /** True while it still waits behind another build in its zone. */
    queued: boolean;
}

export interface PlotTileProps {
    id: string;
    zone: BuildingZone;
    /** Absolute position/stacking supplied by the parent (`projectTile` output + worldX). */
    style?: CSSProperties;
    /**
     * Present while this position's building is under construction. A plot
     * shows the same ring and countdown a built tile shows for an upgrade,
     * so queuing a construction is visible on the surface rather than
     * leaving the plot looking untouched until it completes.
     */
    upgrade?: BuildingUpgradeProgress;
    /**
     * The construct claiming this plot, if any. Its sprite stands faded on
     * the plot from the moment it is queued, so a construct waiting behind
     * another build is as visible as one already building.
     */
    construct?: PlotConstruct;
}

const WRAPPER_CLASSES = 'absolute flex flex-col items-center gap-2';

const PLOT_CLASSES =
    'w-[150px] h-24 rounded-[14px] border-2 border-dashed border-[color:var(--zone-accent)] bg-space-950/[0.28] flex items-center justify-center';

const PLOT_TRANSFORM = 'perspective(340px) rotateX(52deg)';

/** Positioning context for the progress cluster, kept outside the plot's rotateX so the ring is not skewed with the ground. */
const PLOT_STACK_CLASSES = 'relative';

const PLUS_CLASSES = 'text-[color:var(--zone-accent)] text-4xl font-thin leading-none';

/** The faded construct sprite, standing on the plot's centre — outside the rotateX so it is not flattened with the ground. */
const CONSTRUCT_SPRITE_CLASSES =
    'pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 max-h-[120px] max-w-[130px] object-contain opacity-60 drop-shadow-sprite';

const LABEL_CLASSES = 'text-body text-content-secondary';

/**
 * Empty buildable ground on the planet surface (DS-15). Purely
 * presentational — the caller decides which `surfaceLayout.ts` slot this is
 * and supplies its `projectTile` (DS-13) position via `style`.
 */
export function PlotTile({ id, zone, style, upgrade, construct }: PlotTileProps) {
    const label = construct
        ? `${BUILDING_DEFINITIONS[construct.buildingType].name} · ${construct.queued ? 'Queued' : 'Building'}`
        : 'Empty Plot';

    return (
        <div data-tile-id={id} className={WRAPPER_CLASSES} style={{ ...zoneAccentStyle(zone), ...style }}>
            <div className={PLOT_STACK_CLASSES}>
                <div className={PLOT_CLASSES} style={{ transform: PLOT_TRANSFORM }}>
                    {!construct && (
                        <span aria-hidden="true" className={PLUS_CLASSES}>
                            +
                        </span>
                    )}
                </div>
                {construct && (
                    <img
                        src={getBuildingIcon(construct.buildingType, 1, construct.race)}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        data-testid={`plot-construct-sprite-${id}`}
                        className={CONSTRUCT_SPRITE_CLASSES}
                    />
                )}
                <TileProgressCluster upgrade={upgrade} />
            </div>
            <span className={LABEL_CLASSES} data-testid={`plot-label-${id}`}>{label}</span>
        </div>
    );
}

export default PlotTile;
